import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button, Card, message } from 'antd';
import { ArrowRightOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { ordersApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import OrderForm from '../../components/orders/OrderForm';

const createEmptyItem = () => ({
  item_id: null,
  product_id: null,
  item_name: '',
  quantity: 1,
  unit_id: null,
  sku: '',
  cost_per_item: 0,
  amount: 0,
  item_summary: '',
});

const AddOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const presetProjectId = searchParams.get('project_id');
  const presetClientId = searchParams.get('client_id');
  const returnTo = location.state?.returnTo;
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [lockClient, setLockClient] = useState(false);
  const [lockProject, setLockProject] = useState(false);
  const [options, setOptions] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [initialItems, setInitialItems] = useState([createEmptyItem()]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const createParams = {};
        if (presetProjectId) createParams.project_id = presetProjectId;
        if (presetClientId) createParams.client_id = presetClientId;

        const res = await ordersApi.getCreateData(createParams);
        const data = res?.data || {};
        const selectedProject = data.selected_project || data.project;
        const selectedClient = data.selected_client || data.client;

        const clients = [...(data.clients || [])];
        const projects = [...(data.projects || [])];
        if (
          selectedClient?.id != null &&
          !clients.some((c) => String(c.id) === String(selectedClient.id))
        ) {
          clients.push(selectedClient);
        }
        if (
          selectedProject?.id != null &&
          !projects.some((p) => String(p.id) === String(selectedProject.id))
        ) {
          projects.push({
            id: selectedProject.id,
            project_name: selectedProject.project_name,
          });
        }

        setOptions({ ...data, clients, projects });

        const resolvedProjectId =
          selectedProject?.id ?? (presetProjectId != null ? Number(presetProjectId) : undefined);
        const resolvedClientId =
          selectedClient?.id ?? (presetClientId != null ? Number(presetClientId) : undefined);

        setLockProject(Boolean(resolvedProjectId && (presetProjectId || selectedProject)));
        setLockClient(Boolean(resolvedClientId && (presetClientId || selectedClient)));

        setInitialValues({
          order_number: Number(data.last_order || 0) + 1,
          client_id: resolvedClientId,
          project_id: resolvedProjectId,
          company_address_id: data.company_addresses?.[0]?.id,
          shipping_address: null,
          status: 'pending',
          bank_account_id: undefined,
          category_id: data.categories?.[0]?.id,
          discount_value: 0,
          discount_type: 'percent',
          note: '',
        });
        setInitialItems([createEmptyItem()]);
      } catch (error) {
        message.error('خطا در دریافت اطلاعات ایجاد سفارش');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [presetProjectId, presetClientId]);

  const handleSubmit = async (payload) => {
    setSubmitLoading(true);
    try {
      await ordersApi.create(payload);
      message.success('سفارش با موفقیت ایجاد شد');
      navigate(returnTo || '/orders');
    } catch (error) {
      message.error('خطا در ایجاد سفارش');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                  <ShoppingCartOutlined className="text-emerald-600 dark:text-emerald-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0">ایجاد سفارش</h1>
                </div>
              </div>
              <Button icon={<ArrowRightOutlined />} onClick={() => navigate(returnTo || '/orders')}>
                بازگشت
              </Button>
            </div>
          </div>
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            <Card className="shadow-lg border-0 bg-white dark:bg-gray-800">
              {loading ? (
                <TableSkeleton rows={6} columns={2} />
              ) : (
                <OrderForm
                  options={options}
                  initialValues={initialValues}
                  initialItems={initialItems}
                  isEdit={false}
                  lockClient={lockClient}
                  lockProject={lockProject}
                  submitLoading={submitLoading}
                  onSubmit={handleSubmit}
                />
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AddOrder;
